import { useEffect, useMemo, useState } from "react";
import api, { fmtBRL, fmtInt } from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { RefreshCw, Monitor, Clock, TrendingUp, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

function iso(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

const PERIOD_PRESETS = [
  { id: "today", label: "Hoje", compute: () => { const t = new Date(); return { s: iso(t), e: iso(t) }; } },
  { id: "yesterday", label: "Ontem", compute: () => { const y = addDays(new Date(), -1); return { s: iso(y), e: iso(y) }; } },
  { id: "last_7", label: "Últimos 7d", compute: () => { const t = new Date(); return { s: iso(addDays(t, -6)), e: iso(t) }; } },
  { id: "last_30", label: "Últimos 30d", compute: () => { const t = new Date(); return { s: iso(addDays(t, -29)), e: iso(t) }; } },
  { id: "this_month", label: "Este mês", compute: () => ({ s: iso(startOfMonth(new Date())), e: iso(endOfMonth(new Date())) }) },
  { id: "custom", label: "Personalizado", compute: null },
];

const initialPeriod = PERIOD_PRESETS.find(p => p.id === "last_30").compute();

const COLORS = ["#2c3e50", "#c86a3a", "#d4a442", "#7a8b3f", "#8b5a3c", "#6b7d8f", "#a89664", "#5c6f5c"];

function FilterPill({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="px-3 py-1.5 rounded-full border font-display uppercase tracking-wider text-xs transition-all"
      style={{
        background: active ? "var(--ifl-text)" : "transparent",
        color: active ? "#fff" : "var(--ifl-text)",
        borderColor: active ? "var(--ifl-text)" : "var(--ifl-border)",
      }}
    >
      {children}
    </button>
  );
}

function KPI({ label, value, hint, accent, testId, icon: Icon }) {
  return (
    <div className="ifl-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="label-caps">{label}</div>
        {Icon && <Icon size={14} strokeWidth={1.5} style={{ color: accent || "var(--ifl-muted)" }} />}
      </div>
      <div className="font-display text-3xl tabular-nums" style={{ color: accent || "var(--ifl-text)", letterSpacing: "-0.02em" }}>
        {value}
      </div>
      {hint && <div className="font-body text-xs mt-2" style={{ color: "var(--ifl-muted)" }}>{hint}</div>}
    </div>
  );
}

function timeShort(iso) {
  if (!iso || typeof iso !== "string") return "—";
  return iso.slice(11, 16);
}

export default function POS() {
  const [period, setPeriod] = useState(initialPeriod);
  const [presetId, setPresetId] = useState("last_30");
  const [terminalFilter, setTerminalFilter] = useState("");
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expandedTerm, setExpandedTerm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [sumRes, sessRes] = await Promise.all([
        api.get(`/pos/summary`, { params: { start_date: period.s, end_date: period.e, terminal_id: terminalFilter || undefined } }),
        api.get(`/pos/sessions`, { params: { start_date: period.s, end_date: period.e, terminal_id: terminalFilter || undefined } }),
      ]);
      setSummary(sumRes.data);
      setSessions(sessRes.data?.sessions || []);
    } catch (e) {
      toast.error("Erro carregando POS: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [period.s, period.e, terminalFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const doSync = async () => {
    setSyncing(true);
    try {
      const [devRes, movRes] = await Promise.all([
        api.post(`/sync/pdv/pos-devices`),
        api.post(`/sync/pdv/movements`, { days_back: 30 }),
      ]);
      toast.success(`POS atualizado: ${devRes.data.count} dispositivos, ${movRes.data.sangrias + movRes.data.suprimentos} movimentos`);
      await load();
    } catch (e) {
      toast.error("Erro no sync: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const applyPreset = (p) => {
    setPresetId(p.id);
    if (p.compute) setPeriod(p.compute());
  };

  const byPos = summary?.by_pos || [];
  const totals = summary?.totals || {};

  const chartData = useMemo(
    () => byPos.map((r, i) => ({ ...r, color: COLORS[i % COLORS.length] })),
    [byPos]
  );

  const sessionsByTerm = useMemo(() => {
    const m = {};
    for (const s of sessions) {
      const k = s.terminal_id;
      (m[k] = m[k] || []).push(s);
    }
    return m;
  }, [sessions]);

  const allTerminals = byPos.map(r => r.terminal_id).filter(Boolean);

  return (
    <div className="ifl-container py-8 md:py-12" data-testid="pos-page">
      <PageHeader
        eyebrow="Operação"
        title="Abertura por"
        accent="POS"
        description="Faturamento, tickets e sessão de caixa por terminal (POS). Atualiza a cada 5 minutos."
        actions={
          <button
            onClick={doSync}
            disabled={syncing}
            data-testid="pos-sync-btn"
            className="btn-primary flex items-center gap-2"
          >
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Sincronizando..." : "Sincronizar POS"}
          </button>
        }
        testId="pos-header"
      />

      {/* Period pills */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="pos-period-filters">
        {PERIOD_PRESETS.map(p => (
          <FilterPill
            key={p.id}
            active={presetId === p.id}
            onClick={() => applyPreset(p)}
            testId={`period-${p.id}`}
          >
            {p.label}
          </FilterPill>
        ))}
      </div>

      {/* Custom date range */}
      {presetId === "custom" && (
        <div className="flex flex-wrap items-end gap-3 mb-6">
          <div className="flex flex-col">
            <label className="label-caps mb-1">De</label>
            <input type="date" value={period.s} onChange={(e) => setPeriod({ ...period, s: e.target.value })} className="ifl-input" data-testid="date-start" />
          </div>
          <div className="flex flex-col">
            <label className="label-caps mb-1">Até</label>
            <input type="date" value={period.e} onChange={(e) => setPeriod({ ...period, e: e.target.value })} className="ifl-input" data-testid="date-end" />
          </div>
        </div>
      )}

      {/* Terminal filter */}
      {allTerminals.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8">
          <FilterPill active={terminalFilter === ""} onClick={() => setTerminalFilter("")} testId="term-all">
            Todos os POS
          </FilterPill>
          {byPos.map(r => (
            <FilterPill
              key={r.terminal_id}
              active={terminalFilter === r.terminal_id}
              onClick={() => setTerminalFilter(r.terminal_id)}
              testId={`term-${r.terminal_id}`}
            >
              {r.label}
            </FilterPill>
          ))}
        </div>
      )}

      {loading && !summary ? (
        <div className="ifl-card p-12 text-center" data-testid="pos-loading">
          <RefreshCw size={20} className="animate-spin inline-block mb-3" style={{ color: "var(--ifl-muted)" }} />
          <div className="font-body text-sm" style={{ color: "var(--ifl-muted)" }}>Carregando dados dos POS...</div>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="pos-kpis">
            <KPI
              label="Faturamento"
              value={fmtBRL(totals.revenue || 0)}
              hint={`${byPos.length} POS ativos no período`}
              icon={TrendingUp}
              testId="kpi-revenue"
            />
            <KPI
              label="Tickets"
              value={fmtInt(totals.tickets || 0)}
              hint="Cupons emitidos"
              icon={Monitor}
              testId="kpi-tickets"
            />
            <KPI
              label="Vendas em Dinheiro"
              value={fmtBRL(totals.cash_revenue || 0)}
              hint="Cash flow bruto"
              testId="kpi-cash"
            />
            <KPI
              label="Sangria / Suprimento"
              value={`${fmtBRL(totals.sangria || 0)} / ${fmtBRL(totals.suprimento || 0)}`}
              hint="Movimentos de caixa"
              testId="kpi-mov"
            />
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="ifl-card p-5 mb-8" data-testid="pos-chart">
              <div className="label-caps mb-4">Faturamento por POS</div>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--ifl-border)" />
                    <XAxis dataKey="label" stroke="var(--ifl-muted)" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={60} />
                    <YAxis stroke="var(--ifl-muted)" tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmtBRL(v)} />
                    <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* POS Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="pos-cards">
            {byPos.map((r, i) => {
              const isExpanded = expandedTerm === r.terminal_id;
              const termSessions = sessionsByTerm[r.terminal_id] || [];
              return (
                <div key={r.terminal_id} className="ifl-card p-5 flex flex-col" data-testid={`pos-card-${r.terminal_id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                        <div className="font-display text-lg tracking-tight">{r.label}</div>
                      </div>
                      <div className="text-xs mt-1 font-mono" style={{ color: "var(--ifl-muted)" }}>
                        {r.terminal_id}
                      </div>
                    </div>
                    {r.unregistered && (
                      <span className="text-xs px-2 py-0.5 rounded-full border font-display uppercase tracking-wider" style={{ color: "var(--ifl-muted)", borderColor: "var(--ifl-border)" }}>
                        legacy
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <div className="label-caps mb-1">Faturamento</div>
                      <div className="font-display text-xl tabular-nums">{fmtBRL(r.revenue)}</div>
                    </div>
                    <div>
                      <div className="label-caps mb-1">Tickets</div>
                      <div className="font-display text-xl tabular-nums">{fmtInt(r.tickets)}</div>
                    </div>
                    <div>
                      <div className="label-caps mb-1">Ticket Médio</div>
                      <div className="font-display text-lg tabular-nums">{fmtBRL(r.avg_ticket)}</div>
                    </div>
                    <div>
                      <div className="label-caps mb-1">Itens Vendidos</div>
                      <div className="font-display text-lg tabular-nums">{fmtInt(r.items || 0)}</div>
                    </div>
                  </div>

                  {(r.sangria > 0 || r.suprimento > 0 || r.cash_revenue > 0) && (
                    <div className="border-t pt-3 mt-2 space-y-1" style={{ borderColor: "var(--ifl-border)" }}>
                      {r.cash_revenue > 0 && (
                        <div className="flex justify-between text-xs">
                          <span style={{ color: "var(--ifl-muted)" }}>Cash bruto:</span>
                          <span className="tabular-nums">{fmtBRL(r.cash_revenue)}</span>
                        </div>
                      )}
                      {r.sangria > 0 && (
                        <div className="flex justify-between text-xs">
                          <span style={{ color: "var(--ifl-muted)" }}>Sangrias ({r.sangria_count}):</span>
                          <span className="tabular-nums" style={{ color: "var(--ifl-danger)" }}>−{fmtBRL(r.sangria)}</span>
                        </div>
                      )}
                      {r.suprimento > 0 && (
                        <div className="flex justify-between text-xs">
                          <span style={{ color: "var(--ifl-muted)" }}>Suprimentos ({r.suprimento_count}):</span>
                          <span className="tabular-nums" style={{ color: "var(--ifl-success)" }}>+{fmtBRL(r.suprimento)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-xs font-semibold pt-1 border-t" style={{ borderColor: "var(--ifl-border)" }}>
                        <span>Cash líquido:</span>
                        <span className="tabular-nums">{fmtBRL(r.net_cash)}</span>
                      </div>
                    </div>
                  )}

                  {termSessions.length > 0 && (
                    <button
                      className="mt-3 flex items-center justify-between text-xs px-2 py-1.5 border rounded-md hover:bg-black/5"
                      style={{ borderColor: "var(--ifl-border)" }}
                      onClick={() => setExpandedTerm(isExpanded ? null : r.terminal_id)}
                      data-testid={`pos-sessions-toggle-${r.terminal_id}`}
                    >
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {termSessions.length} sessões de caixa
                      </span>
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  )}

                  {isExpanded && (
                    <div className="mt-3 max-h-64 overflow-y-auto space-y-1" data-testid={`pos-sessions-list-${r.terminal_id}`}>
                      {termSessions.map((s, idx) => (
                        <div key={idx} className="text-xs flex items-center justify-between py-1 border-b" style={{ borderColor: "var(--ifl-border)" }}>
                          <div className="flex flex-col">
                            <span className="tabular-nums">{s.date}</span>
                            <span style={{ color: "var(--ifl-muted)" }}>{timeShort(s.opened_at)} → {timeShort(s.closed_at)}</span>
                          </div>
                          <div className="flex flex-col text-right">
                            <span className="tabular-nums font-semibold">{fmtBRL(s.revenue)}</span>
                            <span style={{ color: "var(--ifl-muted)" }}>{s.tickets} tickets</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {byPos.length === 0 && !loading && (
            <div className="ifl-card p-12 text-center" data-testid="pos-empty">
              <Monitor size={32} className="mx-auto mb-3" style={{ color: "var(--ifl-muted)" }} />
              <div className="font-display text-lg">Sem dados no período</div>
              <div className="font-body text-sm mt-2" style={{ color: "var(--ifl-muted)" }}>
                Selecione outro período ou sincronize os dados.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
